/**
 * CTRL_10 Configuration Panel - Change Management & Incident Response
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, InputNumber, Form } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const CHANGE_FIELDS = [
  { value: 'version', label: 'Version Number' },
  { value: 'impact_assessment_completed', label: 'Impact Assessment Completed' },
  { value: 'rollback_plan_exists', label: 'Rollback Plan Exists' },
  { value: 'testing_completed', label: 'Testing Completed' },
  { value: 'approval_obtained', label: 'Approval Obtained' },
  { value: 'change_type', label: 'Change Type' },
  { value: 'risk_level', label: 'Risk Level' }
];

const DEPLOYMENT_STRATEGIES = [
  { value: 'blue_green', label: 'Blue-Green Deployment', description: 'Two identical environments, switch traffic instantly' },
  { value: 'canary', label: 'Canary Deployment', description: 'Gradual rollout to small percentage of users first' },
  { value: 'rolling', label: 'Rolling Deployment', description: 'Update instances gradually, maintain availability' },
  { value: 'feature_flag', label: 'Feature Flag', description: 'Toggle features without deployment' }
];

const APPROVAL_TEAMS = [
  'ml_team',
  'security_team',
  'compliance_team',
  'data_team',
  'product_team',
  'engineering_lead'
];

function CTRL_10_ConfigPanel({ project, projectId, controlCode }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    require_impact_assessment: true,
    require_rollback_plan: true,
    deployment_strategy: 'canary',
    rollback_threshold_minutes: 30,
    required_change_fields: ['version', 'impact_assessment_completed', 'rollback_plan_exists'],
    approval_required_from: ['ml_team', 'security_team'],
    auto_rollback_on_failure: true
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          require_impact_assessment: configData.require_impact_assessment,
          require_rollback_plan: configData.require_rollback_plan,
          deployment_strategy: configData.deployment_strategy,
          rollback_threshold_minutes: configData.rollback_threshold_minutes,
          required_change_fields: configData.required_change_fields,
          approval_required_from: configData.approval_required_from,
          auto_rollback_on_failure: configData.auto_rollback_on_failure
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_10: Change Management & Incident Response Configuration</h3>
        <p style={{ color: '#666' }}>
          Configure change management and incident response requirements for model deployments and updates
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Change Management Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, model changes will be tracked and validated against change management policies
          </p>
        </Panel>

        {configData.enabled && (
          <Panel header="2. Change Management Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Change Requirements">
                <div style={{ marginBottom: '12px' }}>
                  <Switch
                    checked={configData.require_impact_assessment}
                    onChange={(checked) => updateConfig('require_impact_assessment', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require impact assessment for all changes</span>
                </div>

                <div>
                  <Switch
                    checked={configData.require_rollback_plan}
                    onChange={(checked) => updateConfig('require_rollback_plan', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require rollback plan before deployment</span>
                </div>
              </Form.Item>

              <Form.Item label="Deployment Strategy">
                <Select
                  value={configData.deployment_strategy}
                  onChange={(value) => updateConfig('deployment_strategy', value)}
                  style={{ width: '100%' }}
                >
                  {DEPLOYMENT_STRATEGIES.map(strategy => (
                    <Option key={strategy.value} value={strategy.value}>
                      <div>
                        <strong>{strategy.label}</strong>
                        <div style={{ fontSize: '12px', color: '#666' }}>{strategy.description}</div>
                      </div>
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Preferred deployment strategy for model updates
                </p>
              </Form.Item>

              <Form.Item label="Rollback Time Threshold">
                <InputNumber
                  min={1}
                  max={1440}
                  value={configData.rollback_threshold_minutes}
                  onChange={(value) => updateConfig('rollback_threshold_minutes', value)}
                  style={{ width: '200px' }}
                  addonAfter="minutes"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Maximum time allowed before automatic rollback is triggered (default: 30 minutes)
                </p>
              </Form.Item>

              <Form.Item label="Required Change Fields">
                <Select
                  mode="multiple"
                  value={configData.required_change_fields || []}
                  onChange={(value) => updateConfig('required_change_fields', value)}
                  style={{ width: '100%' }}
                  placeholder="Select required fields for change tracking"
                >
                  {CHANGE_FIELDS.map(field => (
                    <Option key={field.value} value={field.value}>
                      {field.label}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Fields that must be documented for every change
                </p>
              </Form.Item>

              <Form.Item label="Approval Required From">
                <Select
                  mode="tags"
                  value={configData.approval_required_from || []}
                  onChange={(value) => updateConfig('approval_required_from', value)}
                  style={{ width: '100%' }}
                  placeholder="Select teams/roles that must approve changes"
                >
                  {APPROVAL_TEAMS.map(team => (
                    <Option key={team} value={team}>
                      {team.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Teams or roles that must approve changes before deployment (add custom roles by typing)
                </p>
              </Form.Item>

              <Form.Item label="Automatic Rollback">
                <Switch
                  checked={configData.auto_rollback_on_failure}
                  onChange={(checked) => updateConfig('auto_rollback_on_failure', checked)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                  style={{ marginRight: '12px' }}
                />
                <span>Enable automatic rollback on deployment failure</span>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  ⚠️ When enabled, failed deployments will automatically trigger rollback to previous version
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Impact Assessment:</strong> {configData.require_impact_assessment ? 'Required' : 'Optional'}</div>
              <div><strong>Rollback Plan:</strong> {configData.require_rollback_plan ? 'Required' : 'Optional'}</div>
              <div><strong>Deployment Strategy:</strong> {DEPLOYMENT_STRATEGIES.find(s => s.value === configData.deployment_strategy)?.label || 'Not Set'}</div>
              <div><strong>Rollback Threshold:</strong> {configData.rollback_threshold_minutes} minutes</div>
              <div><strong>Required Fields:</strong> {configData.required_change_fields?.join(', ') || 'None'}</div>
              <div><strong>Approval Required:</strong> {configData.approval_required_from?.join(', ') || 'None'}</div>
              <div><strong>Auto-Rollback:</strong> {configData.auto_rollback_on_failure ? 'Enabled' : 'Disabled'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_10_ConfigPanel;
